import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Form from 'components/forms/form';
import Button from 'components/inputs/button';
import ButtonGroup from 'components/buttons/buttonGroup';
import ProcessingButton from 'components/inputs/processingButton';
import FormErrors from 'components/formParts/formErrors';
import Callout from 'components/callout/callout';
import CustomerSelector from '@/components/searchAndSelect/CustomerSelector';
import SupplierSelector from '@/components/searchAndSelect/SupplierSelector';
import { TransactionType, TransactionWrite } from 'types';
import useStorages from 'hooks/useStorages';
import useStorageLimits from 'hooks/useStorageLimits';
import useTransactions from 'hooks/useTransactions';
import { ReactComponent as AddIcon } from 'assets/icons/add.svg';
import Section from '../layout/section';
import TextInput from '../inputs/textInput';
import { decodeFirebaseError } from '@/utils/firebaseHelpers';
import TextareaInput from '../inputs/textareaInput';
import { logger } from '@/utils/secureLogger';

interface CreateTransactionFormProps {
    storageId: string;
    transactionType: TransactionType;
}

const CreateTransactionForm: React.FC<CreateTransactionFormProps> = ({ 
    storageId, 
    transactionType 
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [id, setId] = useState<number | undefined>(undefined);
    const [description, setDescription] = useState('');
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [selectedSupplierId, setSelectedSupplierId] = useState('');

    const isValid = () => {
        return id !== undefined && id > 0;
    };
    
    const [errors, setErrors] = useState<string[]>([]);

    const { lastAddedTransactionId, storage, isCustomersAndSuppliersEnabled } = useStorages({ storageId });
    const { canUseCustomers, canUseSuppliers } = useStorageLimits({ storagePlan: storage?.settings?.storagePlan });
    const customersAndSuppliersEnabled = isCustomersAndSuppliersEnabled();
    const showSupplierField = canUseSuppliers && customersAndSuppliersEnabled;
    const showCustomerField = canUseCustomers && customersAndSuppliersEnabled;
    const { createTransactionAsync, isCreating } = useTransactions({ storageId });

    // Set initial transaction ID
    useEffect(() => {
        setId(lastAddedTransactionId || 1);
    }, [lastAddedTransactionId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid()) return;
        
        setErrors([]);

        const transactionData: TransactionWrite = {
            ids: {
                storageId,
            },
            transactionId: id as number,
            type: transactionType,
            customer: showCustomerField && selectedCustomerId ? { customerId: selectedCustomerId } : undefined,
            supplier: showSupplierField && selectedSupplierId ? { supplierId: selectedSupplierId } : undefined,
            description: description || undefined,
            // New status model: client marks transaction as draft; backend controls finalization
            isDraft: true,
            isFinalized: false,
            _version: 1
        }

        try {
            const result = await createTransactionAsync(transactionData);
            console.log(result)
            const transactionPath = (result as any)?.path;
            // Navigate to the newly created transaction
            logger.info(`Transaction created with ID: ${id}. Navigating to: ${transactionPath}`);
            navigate(`/${transactionPath}`);
        } catch (error) {
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error));
            setErrors([errorMessage]);
        }
    };

    return (
        <Form
            className={`group`}
            onSubmit={handleSubmit}
            isLoading={isCreating}
        >

            <Section>

                <Callout type="info" id={transactionType === TransactionType.INCOMING ? 'calloutCreateIncomingTransaction' : 'calloutCreateOutgoingTransaction'}>
                    {transactionType === TransactionType.INCOMING 
                        ? t('createIncomingTransactionInfo')
                        : t('createOutgoingTransactionInfo')
                    }
                </Callout>

                <TextInput
                    label={t('labelId')}
                    value={id ? id.toString() : ''}
                    onChange={(e) => setId(Number(e.target.value))}
                    required
                />

                <TextareaInput
                    label={t('labelDescription')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />

                {transactionType === TransactionType.INCOMING && showSupplierField && (
                    <div className="input-holder-wide">
                        <label>{t('labelSupplier')}</label>
                        <SupplierSelector
                            storageId={storageId}
                            selectedSupplierId={selectedSupplierId}
                            onSupplierSelect={setSelectedSupplierId}
                            placeholder={t('selectSupplier', 'Select a supplier')}
                        />
                    </div>
                )}

                {transactionType === TransactionType.OUTGOING && showCustomerField && (
                    <div className="input-holder-wide">
                        <label>{t('labelCustomer')}</label>
                        <CustomerSelector
                            storageId={storageId}
                            selectedCustomerId={selectedCustomerId}
                            onCustomerSelect={setSelectedCustomerId}
                            placeholder={t('selectCustomer', 'Select a customer')}
                        />
                    </div>
                )}

                <ButtonGroup>
                    {isCreating ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <>
                            <Button wideOnMobile 
                                type="button"
                                design="primary" 
                                disabled={!isValid()}
                                onClick={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
                            >
                                <AddIcon />
                                <span>{t('buttonCreateAsDraft', 'Create as Draft')}</span>
                            </Button>
                        </>
                    )}
                </ButtonGroup>

            </Section>

            {errors.length > 0 && <FormErrors errors={errors} />}
        </Form>
    );
};

export default CreateTransactionForm;
